import { n as encodeBase64Url } from "../base64url-CsUtSmys.js";
import { n as utf8Encode } from "../utf8--X9rtiRi.js";
//#region ../src/storage/share-link.ts
/**
* Share-link normalization: turns a *share page* link copied out of a cloud
* storage UI into the provider's raw-bytes download URL — the one that
* actually supports HTTP Range reads and CORS, as opposed to an HTML preview
* page.
*
* - Dropbox `www.dropbox.com/scl/fi/…` (and legacy `/s/…`) → the
*   `dl.dropboxusercontent.com` content host (drops `dl=`).
* - GitHub `github.com/u/r/blob|raw/branch/path` → `raw.githubusercontent.com`.
* - Google Drive `file/d/<id>`, `open?id=`, `uc?id=` → with a
*   `corsProxyBaseUrl`, the site worker's Drive proxy (`<base>?id=<id>`) —
*   the keyless form that actually works in browsers, since the raw
*   usercontent endpoint 403s any request carrying
*   `Sec-Fetch-Site: cross-site` (every real browser fetch). Precedence:
*   proxy → API key (`drive/v3/files/<id>?alt=media`) → the raw
*   `drive.usercontent.google.com/download?…&confirm=t` form (206 +
*   `Accept-Ranges` for non-browser clients; `confirm=t` skips the
*   virus-scan HTML interstitial). A directly-pasted usercontent link is
*   rewritten to the proxy too — but ONLY when a proxy is configured;
*   otherwise it passes through byte-identical (never re-canonicalized,
*   never switched onto drive/v3 by a key alone).
* - OneDrive: new-style `1drv.ms/<t>/c/<cid>/<shareId>` links (accounts on the
*   SharePoint backend, where the legacy shares API answers 401) → the
*   `my.microsoftpersonalcontent.com/personal/<cid>/_layouts/15/download.aspx
*   ?share=<shareId>` form — serves 206 + `Accept-Ranges` +
*   `Access-Control-Allow-Origin: *` anonymously, even to browser-shaped
*   requests: Range + CORS with no proxy. Legacy links → the shares API
*   (`/v1.0/shares/u!<base64url>/root/content`), which 302s to a temporary
*   download URL; `fetch` follows the redirect.
*
* Anything else — already-direct URLs, CORS-proxy URLs (relative or absolute),
* unknown hosts — passes through byte-identical, so this layer is invisible
* unless a known share page is recognized. Everything downstream (probe, byte
* sources, fallback) stays provider-agnostic.
*/
/**
* Origin used only to parse a RELATIVE `corsProxyBaseUrl`, then stripped back
* off. `.invalid` is reserved by RFC 2606 and can never resolve, so it cannot
* be mistaken for a real destination if it ever leaked into an output.
*/
const RELATIVE_SENTINEL_ORIGIN = "https://relative.invalid";
/** `new URL(...)`, but `null` instead of a throw. */
function tryParseUrl(value, base) {
	try {
		return base === void 0 ? new URL(value) : new URL(value, base);
	} catch {
		return null;
	}
}
/** Rewrite a known share-page link to its raw download form; else return as-is. */
function normalizeShareUrl(rawUrl, opts = {}) {
	let url;
	try {
		url = new URL(rawUrl);
	} catch {
		return rawUrl;
	}
	return resolveKnownProvider(url, rawUrl, opts) ?? rawUrl;
}
/** The provider dispatch: a raw download URL, or null when not a share page. */
function resolveKnownProvider(url, rawUrl, opts) {
	switch (url.hostname) {
		case "dropbox.com":
		case "www.dropbox.com": return normalizeDropbox(url);
		case "github.com": return normalizeGithub(url);
		case "drive.google.com": return normalizeGoogleDrive(url, opts);
		case "drive.usercontent.google.com": return opts.corsProxyBaseUrl !== void 0 && opts.corsProxyBaseUrl !== "" ? normalizeGoogleDrive(url, opts) : null;
		case "1drv.ms":
		case "onedrive.live.com": return normalizeOneDrive(url, rawUrl);
		default: return null;
	}
}
function normalizeDropbox(url) {
	if (!url.pathname.startsWith("/scl/fi/") && !url.pathname.startsWith("/s/")) return null;
	const direct = new URL(url);
	direct.hostname = "dl.dropboxusercontent.com";
	direct.searchParams.delete("dl");
	return direct.toString();
}
function normalizeGithub(url) {
	const m = /^\/([^/]+)\/([^/]+)\/(?:blob|raw)\/(.+)$/.exec(url.pathname);
	if (!m) return null;
	return `https://raw.githubusercontent.com/${m[1]}/${m[2]}/${m[3]}`;
}
/**
* `<proxyBase>?id=<fileId>`, or `null` when no proxy is configured or its base
* cannot be parsed.
*
* Built via URL/searchParams, never concatenation: a base already carrying a
* query (or a trailing `?`) must still yield one valid URL, and searchParams
* encodes the id so it stays one opaque value.
*
* Parsed against a SENTINEL ORIGIN because the documented form of this option
* is RELATIVE — its own JSDoc names `/api/drive-proxy` — and
* `new URL('/api/drive-proxy')` throws. That `TypeError` escaped this module
* and `openRemoteArchive` with it, against a sidecar promising "never throws"
* (PR #375 review). An absolute base ignores the sentinel; a relative one
* resolves against it and is stripped back to path+query, so the caller gets
* back the same shape it configured.
*
* Extracted rather than inlined: folding these branches into
* `normalizeGoogleDrive` took it to a complexity of 12 against a limit of 10,
* and the precedence chain there reads better as three one-line tiers.
*/
function proxiedDriveUrl(corsProxyBaseUrl, id) {
	if (corsProxyBaseUrl === void 0 || corsProxyBaseUrl === "") return null;
	const proxied = tryParseUrl(corsProxyBaseUrl, RELATIVE_SENTINEL_ORIGIN);
	if (proxied === null) return null;
	proxied.searchParams.set("id", id);
	return proxied.origin === RELATIVE_SENTINEL_ORIGIN ? `${proxied.pathname}${proxied.search}` : proxied.toString();
}
function normalizeGoogleDrive(url, opts) {
	const id = /^\/file\/d\/([^/]+)/.exec(url.pathname)?.[1] ?? url.searchParams.get("id");
	if (id === null || id === void 0 || id === "") return null;
	const { corsProxyBaseUrl, googleDriveApiKey: apiKey } = opts;
	const proxied = proxiedDriveUrl(corsProxyBaseUrl, id);
	if (proxied !== null) return proxied;
	const safeId = encodeURIComponent(id);
	if (apiKey !== void 0 && apiKey !== "") return `https://www.googleapis.com/drive/v3/files/${safeId}?alt=media&key=${encodeURIComponent(apiKey)}`;
	return `https://drive.usercontent.google.com/download?id=${safeId}&export=download&confirm=t`;
}
function normalizeOneDrive(url, rawUrl) {
	const m = /^\/[a-z]\/c\/([0-9a-fA-F]+)\/([\w!-]+)$/.exec(url.pathname);
	if (m) return `https://my.microsoftpersonalcontent.com/personal/${m[1]}/_layouts/15/download.aspx?share=${m[2]}`;
	if (!isOneDriveShareShape(url)) return null;
	return `https://api.onedrive.com/v1.0/shares/u!${encodeBase64Url(utf8Encode(rawUrl))}/root/content`;
}
/** Positively recognized OneDrive share-link shapes; anything else must pass
*  through byte-identical rather than be wrapped in the shares API. */
function isOneDriveShareShape(url) {
	if (url.hostname === "1drv.ms") return /^\/[a-z]{1,2}\//.test(url.pathname);
	return url.pathname.startsWith("/redir") || url.pathname.startsWith("/embed") || url.searchParams.has("resid") || url.searchParams.has("id") || url.searchParams.has("cid");
}
//#endregion
export { normalizeShareUrl };
